import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

// Categories that represent services (not products)
const SERVICE_CATEGORIES = new Set([
  'roofing', 'hvac', 'plumbing', 'electrical', 'painting', 'flooring',
  'windows-doors', 'fencing', 'landscaping', 'pest-control', 'pool-installation',
  'solar-panels', 'generators', 'other-home-property', 'auto-repair', 'tires',
  'detailing', 'body-shop', 'other-vehicle-transportation', 'health-insurance',
  'dental-care', 'vision-care', 'mental-health', 'physical-therapy', 'gym-fitness',
  'personal-training', 'nutrition-dietitian', 'chiropractic', 'cosmetic-procedures',
  'home-refinance', 'home-purchase', 'car-insurance', 'travel-insurance',
  'weddings', 'birthdays', 'funerals', 'childcare', 'private-school',
  'gym-memberships', 'courses', 'other-life-events', 'other-travel-experiences',
  // legacy category IDs
  'homes', 'refinancing', 'cars',
]);

const isServiceStack = (category) => SERVICE_CATEGORIES?.has(category);

const categoryConfig = {
  cars: { icon: 'Car', gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)', label: 'Vehicle & Auto' },
  homes: { icon: 'Home', gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)', label: 'Home & Property' },
  refinancing: { icon: 'CreditCard', gradient: 'linear-gradient(135deg, #E0528A, #F58A54)', label: 'Financial' },
  furniture: { icon: 'Sofa', gradient: 'linear-gradient(135deg, #F58A54, #E0528A)', label: 'Home Goods' },
  electronics: { icon: 'Laptop', gradient: 'linear-gradient(135deg, #F06F6B, #F58A54)', label: 'Tech & Electronics' },
};

const statusConfig = {
  active: { label: 'Active', bg: 'rgba(240,111,107,0.12)', color: '#F06F6B', dot: '#F06F6B' },
  pending: { label: 'Pending', bg: 'rgba(245,138,84,0.12)', color: '#F58A54', dot: '#F58A54' },
  completed: { label: 'Completed', bg: 'rgba(16,185,129,0.12)', color: '#10B981', dot: '#10B981' },
};

const ProjectCard = ({ project, onDelete }) => {
  const navigate = useNavigate();

  const cat = categoryConfig?.[project?.category] || { icon: 'Package', gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)', label: project?.category };
  const status = statusConfig?.[project?.status] || { label: project?.status, bg: 'var(--muted)', color: 'var(--text-secondary)', dot: '#9CA3AF' };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    return Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
  };

  const handleViewComparison = () => navigate(`/project-comparison?project=${project?.id}`);
  const handleEditProject = () => navigate(`/create-project-flow?edit=${project?.id}`);
  const handleViewMilestones = () => navigate(`/project-milestone-tracker?project=${project?.id}`);

  const quotesProgress = project?.vendorCount > 0
    ? Math.round((project?.quotesReceived / project?.vendorCount) * 100)
    : 0;

  const showMilestones = isServiceStack(project?.category);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group">
      {/* Card Top Accent */}
      <div className="h-1.5 w-full" style={{ background: cat?.gradient }} />
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200"
              style={{ background: cat?.gradient }}
            >
              <Icon name={cat?.icon} size={20} color="white" />
            </div>
            <div>
              <h3 className="font-bold text-foreground text-base leading-tight">{project?.name}</h3>
              <p className="text-text-secondary text-xs mt-0.5">{cat?.label}</p>
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0"
            style={{ background: status?.bg, color: status?.color }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: status?.dot }} />
            {status?.label}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-muted rounded-xl p-2.5 text-center">
            <div className="text-sm font-bold text-foreground">{project?.vendorCount || 0}</div>
            <div className="text-xs text-text-secondary mt-0.5">Vendors</div>
          </div>
          <div className="bg-muted rounded-xl p-2.5 text-center">
            <div className="text-sm font-bold text-foreground">{project?.quotesReceived || 0}</div>
            <div className="text-xs text-text-secondary mt-0.5">Quotes</div>
          </div>
          <div className="bg-muted rounded-xl p-2.5 text-center">
            <div className="text-sm font-bold text-foreground">{formatDate(project?.createdDate)?.split(',')?.[0]}</div>
            <div className="text-xs text-text-secondary mt-0.5">Created</div>
          </div>
        </div>

        {/* Quote Progress */}
        {project?.vendorCount > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-text-secondary font-medium">Quote Progress</span>
              <span className="font-semibold" style={{ color: '#F06F6B' }}>{project?.quotesReceived}/{project?.vendorCount}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${quotesProgress}%`, background: cat?.gradient }}
              />
            </div>
          </div>
        )}

        {/* Deadline */}
        {project?.deadline && (
          <div className="flex items-center justify-between text-xs mb-4 px-3 py-2 bg-muted rounded-xl">
            <div className="flex items-center gap-1.5 text-text-secondary">
              <Icon name="Calendar" size={12} />
              <span>Deadline</span>
            </div>
            <span className={`font-semibold ${
              getDaysUntilDeadline(project?.deadline) <= 3 ? 'text-error' :
              getDaysUntilDeadline(project?.deadline) <= 7 ? 'text-warning' : 'text-foreground'
            }`}>
              {getDaysUntilDeadline(project?.deadline) > 0
                ? `${getDaysUntilDeadline(project?.deadline)}d left`
                : 'Overdue'}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {project?.status !== 'draft' && (
            <button
              onClick={handleViewComparison}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-white transition-all duration-200 hover:opacity-90 hover:shadow-md"
              style={{ background: cat?.gradient }}
            >
              <Icon name="BarChart3" size={13} color="white" />
              Compare
            </button>
          )}
          {showMilestones && (
            <button
              onClick={handleViewMilestones}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all duration-200"
              title="View milestones"
            >
              <Icon name="Flag" size={13} />
              Milestones
            </button>
          )}
          <button
            onClick={handleEditProject}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <Icon name="Edit" size={13} />
            Edit
          </button>
          {onDelete && (
            <button
              onClick={() => onDelete(project?.id)}
              className="flex items-center justify-center px-3 py-2.5 rounded-xl text-xs font-semibold border border-border text-text-secondary hover:text-red-500 hover:border-red-300 hover:bg-red-50 transition-all duration-200"
              title="Delete stack"
            >
              <Icon name="Trash2" size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;